import { useEffect, useState, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { Briefcase, Globe, Lock, type LucideIcon } from 'lucide-react';
import { trackProductEvent } from '../../lib/product-analytics';
import { openMarketingLink } from '../../lib/marketing-links';
import { useLocale } from '../../hooks/useLocale';
import { MaterialSymbol } from '../icons/MaterialSymbol';
import { WebSaveButton } from '../cv/forms/web-form-elements';
import { useOnboarding, type ProfileVisibility } from './OnboardingProvider';
import { OnboardingFooterNote, OnboardingWebStep } from './web-step-chrome';

const OPTIONS: { key: ProfileVisibility; icon: LucideIcon }[] = [
  { key: 'public', icon: Globe },
  { key: 'employers', icon: Briefcase },
  { key: 'private', icon: Lock },
];

export function StepWhoSeesCV() {
  const { t } = useTranslation('auth');
  const { locale } = useLocale();
  const {
    prevStep,
    nextStep,
    profileVisibility,
    setProfileVisibility,
    publicIndexingAcknowledged,
    setPublicIndexingAcknowledged,
  } = useOnboarding();
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Choosing Public IS the indexing acknowledgment the API requires (owner
  // request 2026-10-04: a second checkbox under Public confused people). The
  // Public card itself says search engines can index the CV and the footer
  // says it can be changed anytime; Public is never pre-selected (the default
  // is Employers only), so it is always the person's own tap. A Public restored
  // from their saved profile was consented when it was saved, so it stays
  // acknowledged instead of locking the button.
  useEffect(() => {
    const acknowledged = profileVisibility === 'public';
    if (publicIndexingAcknowledged !== acknowledged) {
      setPublicIndexingAcknowledged(acknowledged);
    }
  }, [profileVisibility, publicIndexingAcknowledged, setPublicIndexingAcknowledged]);

  const handleContinue = async () => {
    if (profileVisibility === 'public' && !publicIndexingAcknowledged) return;
    trackProductEvent('onboarding_visibility', {
      visibility: profileVisibility,
    });
    setIsSubmitting(true);
    try {
      await nextStep();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <OnboardingWebStep
      titleBase={t('onboarding.whoSeesCV.titleBase', {
        defaultValue: 'Who sees',
      })}
      titleAccent={t('onboarding.whoSeesCV.titleAccent', {
        defaultValue: 'your CV',
      })}
      subtitle={t('onboarding.whoSeesCV.subtitle', {
        defaultValue: 'Your privacy, your call. Change it anytime',
      })}
      onBack={prevStep}
    >
      <div role="radiogroup" style={listStyle}>
        {OPTIONS.map(({ key, icon: Icon }) => {
          const active = profileVisibility === key;
          return (
            <VisibilityCard
              key={key}
              active={active}
              onSelect={() => {
                setProfileVisibility(key);
                setPublicIndexingAcknowledged(key === 'public');
              }}
              icon={<Icon size={23} color="var(--text-primary)" strokeWidth={1.85} />}
              title={t(`onboarding.whoSeesYou.${key}.title`)}
              subtitle={t(`onboarding.whoSeesYou.${key}.subtitle`)}
              badge={
                key === 'public'
                  ? t('onboarding.whoSeesCV.recommended')
                  : undefined
              }
            />
          );
        })}
      </div>

      <OnboardingFooterNote
        text={t('onboarding.whoSeesCV.footerText')}
        linkLabel={t('onboarding.whoSeesCV.privacyLink')}
        onLinkClick={() => {
          void openMarketingLink('privacy', locale);
        }}
      />
      <WebSaveButton
        label={t('onboarding.whoSeesCV.ctaLabel')}
        onClick={handleContinue}
        loading={isSubmitting}
        disabled={profileVisibility === 'public' && !publicIndexingAcknowledged}
      />
    </OnboardingWebStep>
  );
}

/** Selectable option card: the StepChooseAI plan-card recipe (tinted fill +
 * trailing check_circle / radio_button_unchecked). */
function VisibilityCard({
  active,
  onSelect,
  icon,
  title,
  subtitle,
  badge,
}: {
  active: boolean;
  onSelect: () => void;
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  /** Optional promo mark (the StepChooseAI gradient badge recipe). */
  badge?: string;
}) {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      type="button"
      role="radio"
      aria-checked={active}
      onClick={onSelect}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        ...cardStyle,
        backgroundColor: active ? 'rgba(0,0,0,0.06)' : hovered ? 'rgba(0,0,0,0.05)' : 'rgba(0,0,0,0.04)',
      }}
    >
      <span style={iconBoxStyle}>{icon}</span>
      <span style={cardTextStyle}>
        <span style={cardTitleRowStyle}>
          <span style={cardTitleStyle}>{title}</span>
          {badge ? <span style={cardBadgeStyle}>{badge}</span> : null}
        </span>
        <span style={cardSubtitleStyle}>{subtitle}</span>
      </span>
      <MaterialSymbol
        name={active ? 'check_circle' : 'radio_button_unchecked'}
        size={22}
        style={{
          color: active ? 'var(--text-primary)' : 'var(--text-tertiary)',
          flexShrink: 0,
        }}
      />
    </button>
  );
}

const listStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
  marginTop: 4,
};

const cardStyle: CSSProperties = {
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  gap: 14,
  padding: '14px 18px',
  textAlign: 'start',
  color: 'var(--text-primary)',
  border: 'none',
  borderRadius: 16,
  fontFamily: 'inherit',
  cursor: 'pointer',
  transition: 'background-color 150ms',
};

const iconBoxStyle: CSSProperties = {
  width: 34,
  height: 40,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
};

const cardTextStyle: CSSProperties = {
  flex: 1,
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: 3,
};

const cardTitleRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 7,
  minWidth: 0,
};

const cardTitleStyle: CSSProperties = {
  color: 'var(--text-primary)',
  fontSize: 15,
  fontWeight: 500,
};

// Same geometry/gradient as the StepChooseAI plan badge.
const cardBadgeStyle: CSSProperties = {
  padding: '3px 8px',
  borderRadius: 20,
  background: 'linear-gradient(90deg, #8467fd, #5a2aff)',
  color: '#ffffff',
  fontSize: 9,
  fontWeight: 700,
  letterSpacing: 0.2,
  flexShrink: 0,
};

const cardSubtitleStyle: CSSProperties = {
  color: 'var(--text-secondary)',
  fontSize: 13,
  lineHeight: '18px',
};
